import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { LabOSTopbar, useLabOS } from '@/lib/labos';
import { Workflow, Shield, Sun, Moon, History, Settings, KeyRound, Grid3x3, Users, Puzzle, Code2, BarChart3 } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useRoles, hasRole } from '@/hooks/useRoles';
import ErrorBoundary from '@/components/ErrorBoundary';

function useDarkMode() {
  const [dark, setDark] = useState(() => {
    if (typeof window === 'undefined') return false;
    return (
      localStorage.getItem('theme') === 'dark' ||
      (!localStorage.getItem('theme') && window.matchMedia('(prefers-color-scheme: dark)').matches)
    );
  });

  useEffect(() => {
    const root = document.documentElement;
    if (dark) {
      root.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      root.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  }, [dark]);

  return [dark, () => setDark((d) => !d)] as const;
}

export default function AppLayout() {
  const location = useLocation();
  const [dark, toggleDark] = useDarkMode();
  const { roles, loading: rolesLoading } = useRoles();
  const { user } = useLabOS();

  // Check if user has workload-manager role
  const hasWorkloadManagerRole = hasRole(roles, 'workload-manager');
  // Check if user is admin
  const isAdmin = user?.isAdmin || false;

  return (
    <div className="flex flex-col h-screen bg-background">
      <LabOSTopbar appName="AICS - Workflow Runner" accentColor="#8b5cf6" />
      <nav className="border-b bg-background px-4 py-2 flex items-center gap-4">
        <Link
          to="/"
          className={cn(
            'text-sm font-medium transition-colors flex items-center gap-1.5',
            location.pathname === '/' || location.pathname.startsWith('/run')
              ? 'text-foreground'
              : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Workflow className="h-4 w-4" />
          Workflows
        </Link>
        <Link
          to="/history"
          className={cn(
            'text-sm font-medium transition-colors flex items-center gap-1.5',
            location.pathname.startsWith('/history') ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <History className="h-4 w-4" />
          History
        </Link>
        <Link
          to="/outputs"
          className={cn(
            'text-sm font-medium transition-colors flex items-center gap-1.5',
            location.pathname === '/outputs' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Grid3x3 className="h-4 w-4" />
          Outputs
        </Link>
        <Link
          to="/tokens"
          className={cn(
            'text-sm font-medium transition-colors flex items-center gap-1.5',
            location.pathname === '/tokens' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <KeyRound className="h-4 w-4" />
          Tool Key
        </Link>
        <Link
          to="/integrations"
          className={cn(
            'text-sm font-medium transition-colors flex items-center gap-1.5',
            location.pathname === '/integrations' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Puzzle className="h-4 w-4" />
          Integrations
        </Link>
        <Link
          to="/dev"
          className={cn(
            'text-sm font-medium transition-colors flex items-center gap-1.5',
            location.pathname === '/dev' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Code2 className="h-4 w-4" />
          Dev
        </Link>
        <div className="flex-1" />
        {!rolesLoading && hasWorkloadManagerRole && (
          <>
            <Link
              to="/admin/workflows"
              className={cn(
                'text-sm font-medium transition-colors flex items-center gap-1.5',
                location.pathname.startsWith('/admin/workflows')
                  ? 'text-foreground'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Shield className="h-4 w-4" />
              Workflow Manager
            </Link>
            <Link
              to="/admin/groups"
              className={cn(
                'text-sm font-medium transition-colors flex items-center gap-1.5',
                location.pathname === '/admin/groups' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Users className="h-4 w-4" />
              Groups
            </Link>
            <Link
              to="/admin/analytics"
              className={cn(
                'text-sm font-medium transition-colors flex items-center gap-1.5',
                location.pathname === '/admin/analytics' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <BarChart3 className="h-4 w-4" />
              Analytics
            </Link>
            {isAdmin && (
              <Link
                to="/admin/settings"
                className={cn(
                  'text-sm font-medium transition-colors flex items-center gap-1.5',
                  location.pathname === '/admin/settings' ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <Settings className="h-4 w-4" />
                Settings
              </Link>
            )}
            <div className="h-4 w-px bg-border" />
          </>
        )}
        <button
          onClick={toggleDark}
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
        >
          {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
        </button>
      </nav>
      <main className="flex-1 overflow-auto">
        {/* Isolate page crashes so the nav stays usable; recover on navigation. */}
        <ErrorBoundary resetKey={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </main>
    </div>
  );
}
